import type { FormEvent } from 'react';
import { Button } from '@/components/Button';
import { Field, controlClass } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { Section } from '@/components/Section';
import { brand, contact } from '@/content/site';
import { useContactForm } from '@/hooks/useContactForm';
import styles from './Contact.module.css';

const TITLE_ID = 'contact-title';

export function Contact() {
  const { values, errors, status, setField, submit, reset } = useContactForm();
  const submitting = status === 'submitting';

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return (
    <Section id="contact" tone="raised" labelledBy={TITLE_ID}>
      <div className={styles.layout}>
        <Reveal className={styles.aside}>
          <span className={styles.eyebrow}>contact</span>
          <h2 id={TITLE_ID} className={styles.title}>
            {contact.heading}
          </h2>
          <p className={styles.lead}>{contact.lead}</p>

          <ul className={styles.points}>
            {contact.points.map((point) => (
              <li key={point} className={styles.point}>
                <Icon name="check" size={16} />
                {point}
              </li>
            ))}
          </ul>

          <div className={styles.direct}>
            <p className={styles.directRow}>
              <Icon name="mail" size={17} />
              <a href={`mailto:${brand.email}`}>{brand.email}</a>
            </p>
            <p className={styles.directRow}>
              <Icon name="pin" size={17} />
              {brand.location}
            </p>
          </div>
        </Reveal>

        <Reveal className={styles.card} delay={100}>
          {status === 'success' ? (
            <div className={styles.success} role="status">
              <span className={styles.successBadge}>
                <Icon name="check" size={22} />
              </span>
              <h3 className={styles.successTitle}>Thanks — that reached us.</h3>
              <p className={styles.successText}>
                We read every enquiry ourselves and reply within two working days. If it is urgent, email{' '}
                {brand.email} directly.
              </p>
              <Button variant="secondary" onClick={reset}>
                Send another
              </Button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <div className={styles.row}>
                <Field name="name" label="Name" error={errors.name}>
                  {(field) => (
                    <input
                      {...field}
                      className={controlClass}
                      type="text"
                      name="name"
                      autoComplete="name"
                      placeholder="Alex Papadopoulos"
                      value={values.name}
                      onChange={(event) => setField('name', event.target.value)}
                    />
                  )}
                </Field>

                <Field name="email" label="Email" error={errors.email}>
                  {(field) => (
                    <input
                      {...field}
                      className={controlClass}
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={values.email}
                      onChange={(event) => setField('email', event.target.value)}
                    />
                  )}
                </Field>
              </div>

              <Field name="company" label="Company" optional>
                {(field) => (
                  <input
                    {...field}
                    className={controlClass}
                    type="text"
                    name="company"
                    autoComplete="organization"
                    value={values.company}
                    onChange={(event) => setField('company', event.target.value)}
                  />
                )}
              </Field>

              <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>What do you need?</legend>
                <div className={styles.segmented}>
                  {contact.interests.map((interest) => (
                    <label key={interest} className={styles.option}>
                      <input
                        type="radio"
                        name="interest"
                        value={interest}
                        checked={values.interest === interest}
                        onChange={() => setField('interest', interest)}
                      />
                      <span>{interest}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Field name="budget" label="Indicative budget">
                {(field) => (
                  <select
                    {...field}
                    className={controlClass}
                    name="budget"
                    value={values.budget}
                    onChange={(event) => setField('budget', event.target.value)}
                  >
                    {contact.budgets.map((budget) => (
                      <option key={budget} value={budget}>
                        {budget}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              <Field name="message" label="What are you building?" error={errors.message}>
                {(field) => (
                  <textarea
                    {...field}
                    className={controlClass}
                    name="message"
                    placeholder="The problem, who it is for, and where you are stuck."
                    value={values.message}
                    onChange={(event) => setField('message', event.target.value)}
                  />
                )}
              </Field>

              <div className={styles.actions}>
                <p className={styles.privacy}>
                  We use your details to reply to this enquiry, nothing else.
                </p>
                <Button type="submit" size="lg" icon="arrowRight" disabled={submitting}>
                  {submitting ? 'Sending' : 'Send enquiry'}
                </Button>
              </div>

              <p className={styles.status} role="status" aria-live="polite">
                {status === 'error' && 'Something went wrong sending that. Please try again or email us directly.'}
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </Section>
  );
}
